import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Ticket, TicketMessage } from '@/types/admin';
import { useAuditLog } from './useAuditLog';
import { toast } from 'sonner';

export function useTickets() {
  const [loading, setLoading] = useState(false);
  const { logAction } = useAuditLog();

  const getTickets = useCallback(async (filters?: { status?: string; query?: string }) => {
    setLoading(true);
    try {
      let query = supabase
        .from('support_tickets')
        .select(`
          *,
          assignee:admins(id, user_id, profiles(name, email))
        `)
        .order('created_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.query) {
        query = query.or(`subject.ilike.%${filters.query}%,message.ilike.%${filters.query}%,email.ilike.%${filters.query}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data as unknown) as Ticket[];
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "未知錯誤";
      toast.error('無法獲取工單列表: ' + message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getTicket = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select(`
          *,
          assignee:admins(id, user_id, profiles(name, email)),
          user:profiles(name, email, avatar_url, subscription_tier)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return (data as unknown) as (Ticket & { user: any; assignee: any });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "未知錯誤";
      toast.error('無法獲取工單詳情: ' + message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getTicketMessages = useCallback(async (ticketId: string) => {
    try {
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data as unknown) as TicketMessage[];
    } catch (error: unknown) {
      console.error('Error fetching ticket messages:', error);
      return [];
    }
  }, []);

  const updateTicket = useCallback(async (id: string, updates: Partial<Ticket>) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      await logAction({
        action: 'ticket.update',
        targetType: 'ticket',
        targetId: id,
        details: updates,
      });

      toast.success('工單已更新');
      return (data as unknown) as Ticket;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "未知錯誤";
      toast.error('更新工單失敗: ' + message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [logAction]);

  const sendReply = useCallback(async (ticketId: string, adminId: string, message: string) => {
    try {
      // 1. 新增訊息到 ticket_messages
      const { error: msgError } = await supabase
        .from('ticket_messages')
        .insert({
          ticket_id: ticketId,
          sender_id: adminId,
          is_admin: true,
          message,
        });

      if (msgError) throw msgError;

      // 2. 更新工單狀態為 in_progress (如果是 open 的話)
      await supabase
        .from('support_tickets')
        .update({ status: 'in_progress', updated_at: new Date().toISOString() })
        .eq('id', ticketId)
        .eq('status', 'open');

      await logAction({
        action: 'ticket.reply',
        targetType: 'ticket',
        targetId: ticketId,
        details: { message: message.substring(0, 50) + '...' },
      });

      toast.success('回覆已發送');
      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "未知錯誤";
      toast.error('發送回覆失敗: ' + message);
      return false;
    }
  }, [logAction]);

  return {
    loading,
    getTickets,
    getTicket,
    getTicketMessages,
    updateTicket,
    sendReply,
  };
}
